import type { ReactNode } from 'react';

// The queue lives apart from the Toaster so a page can raise toasts without
// downloading the Toaster: a toast raised before a Toaster mounts waits here
// and shows when one does.
export type ToastPosition =
   | 'top-left'
   | 'top-center'
   | 'top-right'
   | 'bottom-left'
   | 'bottom-center'
   | 'bottom-right';

export const POSITIONS: ToastPosition[] = [
   'top-left',
   'top-center',
   'top-right',
   'bottom-left',
   'bottom-center',
   'bottom-right',
];

export type ToastId = string | number;
export type ToastVariant = 'default' | 'error' | 'info' | 'loading' | 'success' | 'warning';

export interface ToastAction {
   label: string;
   onClick: () => void;
}

interface ToastProps {
   id: ToastId;
   title: string;
   description?: string;
   action?: ToastAction;
   cancel?: ToastAction;
   /**
    * Picks the colored status icon beside the text (none for `default`) and
    * how the toast is announced: an error interrupts a screen reader, the
    * rest wait their turn.
    */
   variant?: ToastVariant;
   /** Takes the status icon's place, for a message with a mark of its own. */
   icon?: ReactNode;
   /** Milliseconds on screen; `Infinity` stays until dismissed. */
   duration?: number;
   'data-testid'?: string;
}

export type ToastOptions = Omit<ToastProps, 'id' | 'title' | 'variant'> & {
   id?: ToastId;
   position?: ToastPosition;
   /**
    * The toast closed on its own: its time ran out, or a newer toast pushed
    * it past the Toaster's visible cap.
    */
   onAutoClose?: () => void;
   /** The person closed it: the X, Escape, a swipe, an action, or `toast.dismiss`. */
   onDismiss?: () => void;
};

export type CustomToastProps = Omit<ToastProps, 'id'> &
   Pick<ToastOptions, 'id' | 'position' | 'onAutoClose' | 'onDismiss'>;

export const toast = {
   custom: (props: CustomToastProps) => show(props.variant ?? 'default', props.title, props),
   error: (title: string, options?: ToastOptions) => show('error', title, options),
   info: (title: string, options?: ToastOptions) => show('info', title, options),
   /** Stays until replaced: `toast.success(title, { id })` with the returned id, or `toast.dismiss(id)`. */
   loading: (title: string, options?: ToastOptions) =>
      show('loading', title, { ...options, duration: options?.duration ?? Infinity }),
   success: (title: string, options?: ToastOptions) => show('success', title, options),
   warning: (title: string, options?: ToastOptions) => show('warning', title, options),
   /** Closes one toast, or every toast when called without an id. */
   dismiss: (id?: ToastId) => close(id, 'dismiss'),
};

// A page whose toasts belong in a different corner from the Toaster's own binds
// the position once here, so every call site keeps reading `toast.success(...)`
// and a new one cannot forget it. The single Toaster on the page keeps one list
// per corner, so it still shows them where the page asked.
export function toastAt(position: ToastPosition) {
   return {
      dismiss: toast.dismiss,
      custom: (props: Omit<CustomToastProps, 'position'>) => toast.custom({ ...props, position }),
      error: (title: string, options?: ToastOptions) =>
         toast.error(title, { ...options, position }),
      info: (title: string, options?: ToastOptions) => toast.info(title, { ...options, position }),
      loading: (title: string, options?: ToastOptions) =>
         toast.loading(title, { ...options, position }),
      success: (title: string, options?: ToastOptions) =>
         toast.success(title, { ...options, position }),
      warning: (title: string, options?: ToastOptions) =>
         toast.warning(title, { ...options, position }),
   };
}

/* ---- The queue every toast call writes to and every Toaster reads ---- */

export interface ToastRecord {
   id: ToastId;
   title: string;
   description?: string;
   action?: ToastAction;
   cancel?: ToastAction;
   variant: ToastVariant;
   icon?: ReactNode;
   duration?: number;
   position?: ToastPosition;
   onAutoClose?: () => void;
   onDismiss?: () => void;
   testId?: string;
   open: boolean;
   /** Bumped when the same id is raised again, so timers and announcements restart. */
   revision: number;
}

interface ToasterConfig {
   position: ToastPosition;
   visibleToasts: number;
}

export const DEFAULT_DURATION = 4000;
// Toasts raised before a Toaster mounts wait here; past this many the oldest
// is forgotten rather than letting a page with no Toaster grow a queue forever.
const MAX_QUEUED = 20;
const EMPTY: ToastRecord[] = [];

const store = {
   records: EMPTY,
   revision: 0,
   nextId: 1,
   toasters: [] as ToasterConfig[],
   listeners: new Set<() => void>(),
};

export function subscribe(listener: () => void) {
   store.listeners.add(listener);
   return () => {
      store.listeners.delete(listener);
   };
}

export function getSnapshot() {
   return store.records;
}

export function getServerSnapshot() {
   return EMPTY;
}

function commit(next: ToastRecord[]) {
   store.records = next;
   for (const listener of store.listeners) listener();
}

function show(variant: ToastVariant, title: string, options?: ToastOptions): ToastId {
   const id = options?.id ?? store.nextId++;
   store.revision += 1;
   const record: ToastRecord = {
      id,
      title,
      description: options?.description,
      action: options?.action,
      cancel: options?.cancel,
      variant,
      icon: options?.icon,
      duration: options?.duration,
      position: options?.position,
      onAutoClose: options?.onAutoClose,
      onDismiss: options?.onDismiss,
      testId: options?.['data-testid'],
      open: true,
      revision: store.revision,
   };
   // Raising an id that is still on screen updates that toast in place and
   // restarts its clock; the order of calls in one tick is the order applied,
   // so a dismiss followed by a re-fire leaves the toast up. An id that is on
   // its way out comes back as the newest arrival, else its old place at the
   // back of the line would put it straight past the cap again.
   const { records } = store;
   const existing = records.find(r => r.id === id);
   if (existing?.open) {
      commit(records.map(r => (r.id === id ? record : r)));
   } else {
      const others = records.filter(r => r.id !== id);
      commit(
         store.toasters.length === 0 ? [...others, record].slice(-MAX_QUEUED) : [...others, record]
      );
   }
   closeOverflow();
   return id;
}

// Past the Toaster's cap the oldest open toast in that corner closes, the way
// shadcn's `limit` works: the newest are the ones on screen, and nothing
// waits hidden to reappear later.
function closeOverflow() {
   const toaster = store.toasters[0];
   if (!toaster) return;
   const openByCorner = new Map<ToastPosition, ToastRecord[]>();
   for (const record of store.records) {
      if (!record.open) continue;
      const corner = record.position ?? toaster.position;
      openByCorner.set(corner, [...(openByCorner.get(corner) ?? []), record]);
   }
   for (const open of openByCorner.values()) {
      const overflow = open.slice(0, Math.max(0, open.length - toaster.visibleToasts));
      for (const record of overflow) close(record.id, 'overflow');
   }
}

export function close(id: ToastId | undefined, reason: 'dismiss' | 'timeout' | 'overflow') {
   const { records } = store;
   const closing = records.filter(r => r.open && (id === undefined || r.id === id));
   if (closing.length === 0) return;
   const closed = new Set(closing.map(r => r.id));
   // With no Toaster to play the exit, there is nothing to wait for.
   commit(
      store.toasters.length === 0
         ? records.filter(r => !closed.has(r.id))
         : records.map(r => (closed.has(r.id) ? { ...r, open: false } : r))
   );
   for (const r of closing) (reason === 'dismiss' ? r.onDismiss : r.onAutoClose)?.();
}

// Only a closed toast leaves; a toast re-fired mid-exit is open again and stays.
export function remove(id: ToastId) {
   const { records } = store;
   if (records.every(r => r.id !== id || r.open)) return;
   commit(records.filter(r => r.id !== id));
}

/** A mounted Toaster's corner and cap; returns the unmount. */
export function addToaster(toaster: ToasterConfig) {
   store.toasters.push(toaster);
   closeOverflow();
   return () => {
      store.toasters.splice(store.toasters.indexOf(toaster), 1);
   };
}
